import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useMemo, useRef } from "react";
import Login from "./login";
import { ThemeProvider } from "@mui/material/styles";
import {
  Modal,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Button,
  Box,
  CssBaseline,
} from "@mui/material";
import Dashboard from "./dashboard/Dashboard";
import { setUserSession } from "./redux/global/actions";
import Header, { createAleTheme } from "@datalake/towercontrol-banner";
import { Auth } from "aws-amplify";
import { getBufferedClientErrors } from "./redux/global/middleware";

// ALE Unified Application Design System (tokens + MUI theme shared by every ALE app)
const theme = createAleTheme();

function getCookie(name) {
  let nameEQ = name + "=";
  let ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function App() {
  const headerRef = useRef();
  const loading = useSelector((state) => state.loading);
  const dispatch = useDispatch();
  const errors = useSelector((state) => state.errors);
  const userSession = useSelector((state) => state.userSession);
  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [clientError, setClientError] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(
    () => window.innerWidth >= 1200,
  );
  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  const handleCloseErrorModal = () => {
    setErrorModalOpen(false);
  };

  // A request timed out / failed at the network layer, or the server returned a
  // hard error. `getFromServer` broadcasts a "victoria:client-error" event so we
  // can show it on screen — no DevTools needed to see that something broke.
  useEffect(() => {
    const onClientError = (e) => setClientError(e.detail);
    window.addEventListener("victoria:client-error", onClientError);
    return () =>
      window.removeEventListener("victoria:client-error", onClientError);
  }, []);

  // Escape hatch: if the API is blocked on the user's side, the report can't be
  // delivered automatically — let them download it and send it to an admin.
  const downloadClientErrorReport = (detail) => {
    const buffered = getBufferedClientErrors();
    const report = {
      generatedAt: new Date().toISOString(),
      current: detail || null,
      pending: buffered,
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `victoria-error-report-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const clientErrorMessage = (detail) => {
    if (!detail) return "";
    if (detail.errorType === "timeout") {
      return `The request to "${detail.resource}" timed out after ${Math.round(
        (detail.timedOutAfterMs || 0) / 1000,
      )}s with no response. This is often a network or corporate-proxy issue. It has been reported to the administrators.`;
    }
    if (detail.errorType === "network") {
      return `The request to "${detail.resource}" could not reach the server (network/CORS). It has been reported to the administrators.`;
    }
    return `The request to "${detail.resource}" failed (HTTP ${detail.responseCode}). It has been reported to the administrators.`;
  };

  const handleLogout = () => {
    localStorage.clear();
    dispatch(setUserSession(null));
  };

  const handleUserSettings = async (event) => {
    if (event.target.id === "Logout") {
      await Auth.signOut();
    }
  };

  useEffect(() => {
    const errorCookie = getCookie("error");
    console.log("errorCookie", errorCookie);
    if (errorCookie === "timeout") {
      setErrorModalOpen(true);
    } else if (errorCookie === "token") {
      handleLogout();
    }
  });

  useMemo(async () => {
    const session = await Auth.currentSession().catch(() => null);
    if (session) {
      const user = session.getIdToken().decodePayload();
      setUsername(user.identities?.[0]?.userId?.[0]?.toUpperCase() || "");
    }
  }, []);

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Modal open={loading} aria-label="Loading">
          <Box
            sx={{
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        </Modal>
        <Modal
          open={errorModalOpen}
          onClose={handleCloseErrorModal}
          style={{
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              p: 3,
              borderRadius: 1.5,
              boxShadow: 24,
              width: 400,
              maxWidth: "80%",
            }}
          >
            <Typography variant="h3" color="primary">
              Oops! Something went wrong.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Your most recent query failed, but the result may be cached.
              Please try again in a minute.
            </Typography>
          </Box>
        </Modal>

        <Snackbar
          open={Boolean(clientError)}
          onClose={() => setClientError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setClientError(null)}
            sx={{ maxWidth: 600 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => downloadClientErrorReport(clientError)}
              >
                Download report
              </Button>
            }
          >
            {clientErrorMessage(clientError)}
            <div style={{ marginTop: 6, fontSize: "0.8rem", opacity: 0.9 }}>
              If the problem persists, click “Download report” and send the file
              to an administrator.
            </div>
          </Alert>
        </Snackbar>

        <Header
          ref={headerRef}
          handleToggleMenu={userSession ? toggleDrawer : undefined}
          handleUserSettings={handleUserSettings}
          username={username}
          zIndex={1300}
        />
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
          {userSession ? (
            <Dashboard
              headerRef={headerRef}
              open={drawerOpen}
              onToggleDrawer={toggleDrawer}
            />
          ) : (
            <Login />
          )}
        </Box>
      </ThemeProvider>
    </div>
  );
}

export default App;
